import { motion } from "framer-motion";
import { Sparkles, Shield, DollarSign, Users, Clock, Award } from "lucide-react";

const reasons = [
  {
    icon: Sparkles,
    title: "Detail-Driven Cleaning",
    desc: "We follow the same checklist on every visit so nothing is missed — kitchens, bathrooms, baseboards, high-touch surfaces, the works.",
    accent: "primary" as const,
  },
  {
    icon: Shield,
    title: "Consistent, Trusted Team",
    desc: "The same standard every single visit. We treat your home or office the way we'd want ours treated.",
    accent: "primary" as const,
  },
  {
    icon: DollarSign,
    title: "Honest, Transparent Pricing",
    desc: "Free written estimates with no hidden fees and no pressure. You'll know exactly what a clean costs before we start.",
    accent: "secondary" as const,
  },
  {
    icon: Clock,
    title: "Weekdays, 8 AM – 5 PM",
    desc: "Monday through Friday scheduling that works around your workday, with recurring plans weekly, bi-weekly, or monthly.",
    accent: "primary" as const,
  },
  {
    icon: Award,
    title: "Germantown Locals",
    desc: "Based right here in Germantown, TN and serving Collierville, Cordova, Bartlett, Eads, East Memphis, and Memphis.",
    accent: "secondary" as const,
  },
  {
    icon: Users,
    title: "Locally Owned & Operated",
    desc: "Owner Magaly Morales personally stands behind every clean. You're working directly with the people doing the work.",
    accent: "primary" as const,
  },
];

const WhyChooseUs = () => {
  return (
    <section className="section-padding bg-accent relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />

      <div className="container-max relative z-10">
        <div className="text-center mb-14 md:mb-16">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-secondary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Why MGL Cleaning Solutions
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-accent-foreground mb-4 tracking-tight font-bold"
          >
            Why Choose <span className="text-gradient-primary">Us</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-accent-foreground/70 max-w-2xl mx-auto text-lg"
          >
            Six reasons families and businesses across the Memphis area trust us to keep their spaces spotless.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {reasons.map((reason, i) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="glass-card rounded-2xl p-6 md:p-8 card-hover-lift"
            >
              <div className={`inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-5 ${
                reason.accent === "secondary"
                  ? "bg-secondary/25 text-secondary"
                  : "bg-primary/25 text-primary-foreground"
              }`}>
                <reason.icon className="h-7 w-7" />
              </div>
              <h3 className="font-heading text-xl text-accent-foreground mb-2 tracking-tight font-bold">{reason.title}</h3>
              <p className="text-accent-foreground/70 text-sm leading-relaxed">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
